"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var _ = require("lodash");
var Offer = /** @class */ (function () {
    function Offer() {
        this.type = null;
        this.discountType = null;
        this.rate = 0;
        this.stages = [];
        this.entries = [];
        this.isMembershipDiscount = false;
    }
    Offer.getDiscountedPrice = function (price, discountRate, discountType, count) {
        if (count === void 0) { count = 1; }
        switch (discountType) {
            case 'PRICE':
                return Math.max(0, price - discountRate / count);
            case 'FIXED':
                return discountRate / count;
            case 'PERCENTAGE':
                return price * (1.0 - discountRate / 100.0);
            default:
                return price;
        }
    };
    Offer.prototype.setType = function (type) {
        this.type = type;
    };
    Offer.prototype.setDiscountType = function (type) {
        this.discountType = type;
    };
    Offer.prototype.setRate = function (rate) {
        this.rate = rate;
    };
    Offer.prototype.setIsMembershipDiscount = function (isMembershipDiscount) {
        this.isMembershipDiscount = isMembershipDiscount;
    };
    Offer.prototype.addEntry = function (entry) {
        this.entries.push(entry);
    };
    Offer.prototype.addStage = function (stage) {
        var price = stage.entry.price;
        this.stages.push(__assign(__assign({}, stage), { price: price }));
    };
    Offer.prototype.getDiscounted = function () {
        var _this = this;
        if (!this.discountType) {
            throw new Error('ERROR_MISSING_OFFER_DATA');
        }
        /**
         * Returns calculated discount for this particular entry/entries
         * Used by each other offer type
         * example:
         * entry1: { price: 10000 }, entry2: { price: 15000 }
         * discount rate: 10
         * results:
         * for PRICE discount type: entry1: { discountedPrice: 9000 }, entry2: { discountedPrice: 14000 }
         * for PERCENTAGE discount type: entry1: { discountedPrice: 9000 }, entry2: { discountedPrice: 13500 }
         * for FIXED discount type: entry1: { discountedPrice: 1000 }, entry2: { discountedPrice: 1000 }
         */
        if (this.type === 'ITEM_DISCOUNT') {
            return this.entries.map(function (entry) {
                var price = entry.price;
                var originalPrice = price;
                price = _this.applyDiscount(price);
                return {
                    discountedPrice: Math.round(price),
                    id: entry.id,
                    originalPrice: originalPrice,
                    vatRate: entry.vatRate,
                };
            });
        }
        // Apply STAGED_INDIVIDUAL offer logic and pass data to ITEM_DISCOUNT
        if (this.type === 'STAGED_INDIVIDUAL') {
            return _.flatMap(this.stages, function (stage) {
                var stageOffer = new Offer();
                stageOffer.setType('ITEM_DISCOUNT');
                stageOffer.setRate(stage.rate);
                stageOffer.setDiscountType(stage.type);
                stageOffer.addEntry(stage.entry);
                return stageOffer.getDiscounted();
            });
        }
        /**
         * Apply BUNDLE or STAGED_BUNDLE offer
         * Spreads PRICE or FIXED discount rate evenly between ENTRIES and passes data to ITEM_DISCOUNT logic
         * PERCENTAGE is multiplicative, so it just being passed to ITEM_DISCOUNT logic
         */
        if (this.type === 'BUNDLE') {
            return _.flatMap(this.entries, function (entry) { return _this.discountBundleItem(_this.entries.length, entry); });
        }
        /**
         * Apply STAGED_BUNDLE offer
         * Spreads PRICE or FIXED discount rate evenly between STAGES and passes data to ITEM_DISCOUNT logic
         * PERCENTAGE is multiplicative, so it just being passed to ITEM_DISCOUNT logic
         */
        if (this.type === 'STAGED_BUNDLE') {
            return _.flatMap(this.stages, function (stage) {
                return _this.discountBundleItem(_this.stages.length, stage.entry);
            });
        }
        /**
         * Apply VISUAL offer
         * Return offer with no discount benefits
         */
        if (this.type === 'VISUAL') {
            return this.entries.map(function (entry) {
                return {
                    discountedPrice: entry.price,
                    id: entry.id,
                    originalPrice: entry.price,
                    vatRate: entry.vatRate,
                };
            });
        }
        if (this.type === 'ORDER_DISCOUNT') {
            var total_1 = _.sumBy(this.entries, function (entry) { return entry.price; });
            // Used with PRICE discounts
            var discountRemaining_1 = this.rate;
            return this.entries.map(function (entry, index) {
                var fractionOfWhole = entry.price / total_1;
                var newPrice = entry.price;
                if (_this.discountType === 'FIXED' || _this.discountType === 'PRICE') {
                    // Calculate discount
                    var discount = _this.rate * fractionOfWhole;
                    // Apply rounding
                    discount = Math.floor(discount);
                    // If this is the last entry, just apply whatever discount we have left
                    // This fixes rounding errors which would otherwise cause the applied
                    // discount to be 0.01 lower than the actual rate
                    if (index >= _this.entries.length - 1) {
                        discount = discountRemaining_1;
                    }
                    // Make sure we dont hit lower bounds
                    // If it's PRICE discount we reduce total by the discount, if its fixed we just set it
                    newPrice = Math.max(0, _this.discountType === 'PRICE' ? entry.price - discount : discount);
                    // Reduce our remaining discount
                    discountRemaining_1 -= discount;
                }
                else if ((_this.discountType === 'PERCENTAGE' && !_this.isMembershipDiscount) ||
                    (_this.isMembershipDiscount && !entry.disableMembershipDiscount)) {
                    newPrice -= entry.price * (_this.rate / 100);
                }
                // Round the price to two decimal points
                newPrice = Math.round(newPrice);
                return {
                    discountedPrice: newPrice,
                    id: entry.id,
                    originalPrice: entry.price,
                    vatRate: entry.vatRate,
                };
            });
        }
        return [];
    };
    Offer.prototype.discountBundleItem = function (count, entry) {
        var stageOffer = new Offer();
        stageOffer.setType('ITEM_DISCOUNT');
        stageOffer.setDiscountType(this.discountType);
        if (this.discountType === 'PRICE' || this.discountType === 'FIXED') {
            stageOffer.setRate(this.rate / count);
        }
        else {
            stageOffer.setRate(this.rate);
        }
        stageOffer.addEntry(entry);
        return stageOffer.getDiscounted();
    };
    Offer.prototype.applyDiscount = function (price) {
        return Offer.getDiscountedPrice(price, this.rate, this.discountType);
    };
    return Offer;
}());
exports.default = Offer;
